import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send, Facebook, Instagram, Twitter, CheckCircle } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://formspree.io/f/mojndwzd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 md:py-20" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-brand-dark mb-4">
            Get in <span className="text-brand-coral">Touch</span>
          </h1>
          <p className="font-body text-base md:text-lg text-brand-muted max-w-2xl mx-auto px-4">
            Have questions about our care packages? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm" data-testid="contact-form-container">
            {isSubmitted ? (
              /* Thank You Message */
              <div className="text-center py-12" data-testid="thank-you-message">
                <div className="w-20 h-20 mx-auto bg-brand-primary/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-brand-primary" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-brand-dark mb-4">Thank You!</h2>
                <p className="font-body text-brand-muted mb-8 max-w-md mx-auto">
                  Your message has been sent successfully. We'll get back to you as soon as possible.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="px-6 py-3 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                  data-testid="send-another-message"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              /* Contact Form */
              <>
                <h2 className="font-heading text-xl md:text-2xl text-brand-dark mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="font-body font-medium text-brand-dark text-sm md:text-base">Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      className="mt-2"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-body font-medium text-brand-dark text-sm md:text-base">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email"
                      className="mt-2"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="font-body font-medium text-brand-dark text-sm md:text-base">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What is this about?"
                      className="mt-2"
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="font-body font-medium text-brand-dark text-sm md:text-base">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Type your message here..."
                      rows={5}
                      className="mt-2"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 md:py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? "Sending..." : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-6 md:space-y-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm" data-testid="contact-info">
              <h2 className="font-heading text-xl md:text-2xl text-brand-dark mb-6">Contact Information</h2>
              <div className="space-y-5 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-brand-primary" size={18} />
                  </div>
                  <div>
                    <h3 className="font-body font-medium text-brand-dark mb-1 text-sm md:text-base">Email</h3>
                    <a href="mailto:comfortsofhometulsa@gmail.com" className="font-body text-brand-primary hover:underline text-sm md:text-base break-all">
                      comfortsofhometulsa@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-coral/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-brand-coral" size={18} />
                  </div>
                  <div>
                    <h3 className="font-body font-medium text-brand-dark mb-1 text-sm md:text-base">Phone</h3>
                    <a href="tel:9186093851" className="font-body text-brand-primary hover:underline text-sm md:text-base">(918) 609-3851</a>
                  </div>
                </div>
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-secondary/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-brand-primary" size={18} />
                  </div>
                  <div>
                    <h3 className="font-body font-medium text-brand-dark mb-1 text-sm md:text-base">Location</h3>
                    <p className="font-body text-brand-muted text-sm md:text-base">Tulsa, Oklahoma</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm" data-testid="social-links">
              <h2 className="font-heading text-xl md:text-2xl text-brand-dark mb-4 md:mb-6">Follow Us</h2>
              <p className="font-body text-brand-muted mb-4 md:mb-6 text-sm md:text-base">
                Stay connected for updates, tips, and more ways to show your student you care.
              </p>
              <div className="flex gap-3 md:gap-4">
                <a 
                  href="#" 
                  className="w-10 h-10 md:w-12 md:h-12 bg-brand-primary/10 rounded-full flex items-center justify-center hover:bg-brand-primary hover:text-white transition-all"
                  aria-label="Facebook"
                  data-testid="contact-social-facebook"
                >
                  <Facebook size={18} className="text-brand-primary" />
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 md:w-12 md:h-12 bg-brand-coral/10 rounded-full flex items-center justify-center hover:bg-brand-coral hover:text-white transition-all"
                  aria-label="Instagram"
                  data-testid="contact-social-instagram"
                >
                  <Instagram size={18} className="text-brand-coral" />
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 md:w-12 md:h-12 bg-brand-secondary/30 rounded-full flex items-center justify-center hover:bg-brand-primary hover:text-white transition-all"
                  aria-label="Twitter"
                  data-testid="contact-social-twitter"
                >
                  <Twitter size={18} className="text-brand-primary" />
                </a>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="bg-brand-secondary/20 rounded-2xl p-6 md:p-8" data-testid="business-hours">
              <h2 className="font-heading text-xl md:text-2xl text-brand-dark mb-4">Delivery Info</h2>
              <p className="font-body text-brand-muted text-sm md:text-base">
                We deliver care packages to University of Tulsa and Oral Roberts University campuses. Delivery times and schedules will vary based on package type and location.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
